/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import InfoIcon from '@atlaskit/icon/core/migration/information--info';
import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from '../messages';

import { CustomDisclaimer, type CustomDisclaimerProps } from './CustomDisclaimer';

const linkStyles = cssMap({
	default: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
		textDecoration: 'none',

		'&:visited': {
			color: token('color.text.subtle'),
		},

		'&:hover': {
			color: token('color.text.subtle'),
			textDecoration: 'underline',
		},
	},
	updated: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
		'&:visited': {
			color: token('color.text.subtle'),
		},
	},
});

export interface FooterDisclaimerProps {
	/**
	 * Custom disclaimer to be displayed in the footer.
	 *
	 */
	disclaimer: CustomDisclaimerProps['disclaimer'] | undefined;
	/** The appearance of main footer. */
	appearance: 'small' | 'medium' | 'large';
}

export function FooterDisclaimer({ disclaimer, appearance }: FooterDisclaimerProps) {
	const { formatMessage } = useIntl();

	return disclaimer ? (
		<CustomDisclaimer disclaimer={disclaimer} />
	) : (
		<Inline space="space.050" alignBlock="center">
			<InfoIcon
				label={formatMessage(messages.infoIconLabel)}
				color={token('color.text.subtle')}
				LEGACY_size="small"
				size="small"
			/>

			<Anchor
				color={token('color.text.subtle')}
				href="https://www.atlassian.com/trust/atlassian-intelligence"
				// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
				xcss={fg('editor_a11y_ai_footer_link_variation') ? linkStyles.updated : linkStyles.default}
			>
				{formatMessage(
					// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
					fg('editor_a11y_ai_footer_link_variation')
						? messages.disclaimerTextUpdated
						: appearance === 'large'
							? messages.disclaimerText
							: messages.disclaimerTextShort,
				)}
			</Anchor>
		</Inline>
	);
}
